import React, { FC, useEffect, useRef, forwardRef } from 'react';

import {
  Text,
  Flex,
  Modal,
  Input,
  Center,
  Spinner,
  Button,
  ModalBody,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
} from '@chakra-ui/react';

import { Form, Formik, useField } from 'formik';

import { useSKU } from 'hooks';
import { useSKUmtn } from 'hooks/useSKU';

import { SiteSelect } from 'components/primitives/SiteSelect';

import FormikInput from 'components/primitives/FormikInput';
import FormikTextArea from 'components/primitives/FormikTextArea';
import FormikDatepicker from 'components/primitives/FormikDatepicker';

import {
  getInitialValues,
  validationSchema,
} from './EditInventoryCountModal.utils';

import {
  InventoryCount,
  InventoryCountStatus,
  ItemType,
} from 'types/inventory';
import { EditInventoryCountModalViewProps } from './EditInventoryCountModal.props';
import FormikSwitch from 'components/primitives/FormikSwitch';
import Alert from 'components/primitives/Alert';
import { toApiPayloadDateFormat } from 'utils/dateUtils';
import { SKU } from 'types/sku';

const EditInventoryCountModalView: FC<EditInventoryCountModalViewProps> = ({
  count,
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  onRefresh,
  switchOnChange,
  archiveAlertRef,
  cancelArchiveAlert,
  proceedArchiveAlert,
  saveAlertRef,
  cancelSaveAlert,
  proceedSaveAlert,
  openSaveAlert,
  toggleSwitch,
  formikRef,
}) => {
  const calculationsRef = useRef({
    items: [] as any[],
    cases: [] as any[],
  });

  return (
    <Modal
      size="6xl"
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent p="0px 10px" borderRadius="15px">
        <Flex
          px="50px"
          shadow="lg"
          position="relative"
          justify="space-between"
          boxShadow="0px 4px 4px rgba(0, 0, 0, 0.1)"
          align="center">
          <ModalHeader
            textAlign="center"
            fontWeight="600"
            color="#262626"
            flex={0.8}>
            Inventory Count Data
          </ModalHeader>
          <Button onClick={onRefresh}>Refresh</Button>
          <ModalCloseButton
            _focus={{ outline: 'none' }}
            top="5"
            onClick={onClose}
          />
        </Flex>

        <ModalBody minH="400px">
          <Flex direction="column" p="40px 80px">
            <Formik
              innerRef={formikRef}
              initialValues={getInitialValues(count)}
              validationSchema={validationSchema}
              enableReinitialize={true}
              onSubmit={(values) => {
                const openItems = values.openItems?.map((i) => ({
                  ...i,
                  sealedQuantity: calculationsRef.current.items?.find(
                    (j) => j.sku === i.sku,
                  )?.totalSealedItems,
                }));
                const openCases = values.openCases?.map((i) => ({
                  ...i,
                  sealedQuantity: calculationsRef.current.cases?.find(
                    (j) => j.sku === i.sku,
                  )?.totalSealedCases,
                }));
                const sealedPackages = values.sealedPackages?.map((i) => ({
                  ...i,
                  sealedQuantity: 0,
                }));

                onSubmit({
                  openItems,
                  openCases,
                  sealedPackages,
                  notes: values?.notes,
                  countDate: toApiPayloadDateFormat(values.countDate),
                });
              }}>
              {({ values, setFieldValue }) => (
                <Form>
                  <Flex direction="column" gap="10px">
                    <Flex gap="20px" align="flex-start">
                      <Flex mt="4px" flex={1} direction="column" gap="5px">
                        <SiteSelect
                          containerProps={{
                            isRequired: true,
                            isDisabled: true,
                          }}
                          onChange={() => {
                            setFieldValue('openItems', []);
                            setFieldValue('openCases', []);
                            setFieldValue('sealedPackages', []);
                          }}
                        />
                      </Flex>
                      <Flex flex={1}>
                        <FormikDatepicker
                          containerProps={{
                            isRequired: true,
                            isDisabled: true,
                          }}
                          disabled={count?.status === InventoryCountStatus.DONE}
                          labelProps={{
                            color: '#333',
                            fontSize: '12px',
                          }}
                          name="countDate"
                          label="Count Date"
                        />
                      </Flex>
                    </Flex>

                    <Flex
                      gap="20px"
                      mt="30px"
                      justify="center"
                      align="center"
                      direction="column">
                      <SealedPackages
                        site={values?.site || ''}
                        isArchived={count?.isArchived || false}
                        count={count}
                      />

                      <OpenItems
                        site={values?.site || ''}
                        onCalculationsUpdate={(_calc) => {
                          calculationsRef.current.items = _calc;
                        }}
                        isArchived={count?.isArchived || false}
                        count={count}
                      />

                      <OpenCases
                        site={values?.site || ''}
                        onCalculationsUpdate={(_calc) => {
                          calculationsRef.current.cases = _calc;
                        }}
                        isArchived={count?.isArchived || false}
                        count={count}
                      />
                      <FormikTextArea
                        name="notes"
                        label="Inventory Count Notes"
                        isDisabled={count?.isArchived || false}
                      />
                    </Flex>
                    {/* <Flex flex={1} alignItems="center">
                      <FormikSwitch
                        name="archive"
                        onChange={switchOnChange}
                        toggleSwitch={toggleSwitch}
                        isDisabled={isLoading}
                      />
                      <Text ml="2">Archive Count</Text>
                    </Flex> */}
                    <Flex mt="50px" gap="20px" justify="center">
                      <Button
                        variant="outline"
                        w="200px"
                        onClick={onClose}
                        isDisabled={isLoading}>
                        Cancel
                      </Button>
                      {count?.harvestCount === 0 && (
                        <Button
                          type="submit"
                          disabled={count?.isArchived || false}
                          w="200px"
                          isLoading={isLoading}>
                          Save
                        </Button>
                      )}
                      {count && count?.harvestCount > 0 && (
                        <Button
                          type="button"
                          onClick={openSaveAlert}
                          disabled={count?.isArchived || false}
                          w="200px"
                          isLoading={isLoading}>
                          Save
                        </Button>
                      )}
                    </Flex>
                  </Flex>
                </Form>
              )}
            </Formik>
            {count && count.harvestCount > 0 && (
              <Alert
                ref={archiveAlertRef}
                title="Archive Inventory Count"
                body={`By marking this Inventory Count as Archived, you are also archiving ${count?.harvestCount} Harvest Summaries, do you want to proceed?`}
                button1="Cancel"
                button2="Proceed"
                onCancelClick={cancelArchiveAlert}
                onSaveClick={proceedArchiveAlert}
                closeOnOverlayClick={false}
              />
            )}
            <Alert
              ref={saveAlertRef}
              title="Edit Inventory Count"
              body={`Are you sure you want to proceed? This action will result in updating ${count?.harvestCount} Harvest Summaries. Please note that it may take some minutes for the changes to fully reflect in all the reports.`}
              button1="Cancel"
              button2="Proceed"
              onCancelClick={cancelSaveAlert}
              onSaveClick={proceedSaveAlert}
              closeOnOverlayClick={false}
            />
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default EditInventoryCountModalView;

const commonProps = {
  p: '20px 40px',
  w: 'full',
  gap: '10px',
  align: 'flex-start',
  border: '1px solid #ccc',
  borderRadius: '15px',
  maxH: '300px',
  overflowY: 'auto',
  direction: 'column',
} as const;

const headingProps = {
  fontSize: '16px',
  fontWeight: '600',
  fontFamily: 'Poppins',
} as const;

type CommonProps = {
  site: string;
  isArchived?: boolean;
  onCalculationsUpdate?: (_calc: any[]) => void;
  count: InventoryCount | null;
};

type FielValue = InventoryCount['openCases'];

const SealedPackages: FC<CommonProps> = ({ site, isArchived, count }) => {
  const { data: skuData = [], mutate, isLoading } = useSKUmtn(
    'package',
    undefined,
    site,
  );
  const editingCount = count?.status !== InventoryCountStatus.SCHEDULED;

  const [field, , helpers] = useField<FielValue>('sealedPackages');
  const packagesSKU = editingCount ? count?.sealedPackages || [] : skuData;

  useEffect(() => {
    if (!packagesSKU || count?.status !== InventoryCountStatus.SCHEDULED) {
      return;
    }

    mutate(undefined, {
      onSuccess: (_packagesSKU) => {
        const fieldValues = _packagesSKU?.map((i) => ({
          sku: i._id,
          quantity: '' as any,
          sealedQuantity: '' as any,
        }));

        helpers.setValue(fieldValues);
      },
    });
  }, [site]);

  const sealedCasesSubTotal = field.value?.reduce(
    (acc, cur) => acc + Number(cur.quantity || '0'),
    0,
  );

  const totalItemsSubTotal = packagesSKU
    ?.map?.((pack, index) => {
      const items = editingCount
        ? (pack as ItemType)?.skuDetails?.items || []
        : (pack as SKU)?.items;
      const packItems =
        items
          ?.map((i) => i.quantity)
          ?.reduce((acc, current) => acc + current, 0) || 0;

      const totalItems =
        Number(field.value?.[index]?.quantity || '0') * packItems;

      return totalItems;
    })
    ?.reduce((acc, cur) => acc + cur, 0);

  return (
    <Flex {...commonProps}>
      <Text {...headingProps}>Full Packages</Text>

      {isLoading ? (
        <Center w="100%" flex={1} p="100px">
          <Spinner />
        </Center>
      ) : (
        <>
          <Flex gap="10px" w="full" fontSize="14px">
            <Flex flex={1}>
              <Text>Package SKUs</Text>
            </Flex>
            <Flex flex={1}>
              <Text>Full Case Count</Text>
            </Flex>
            <Flex flex={1}>
              <Text>Total Items</Text>
            </Flex>
          </Flex>

          <Flex direction="column" gap="10px" w="full">
            {packagesSKU?.map((pack, index) => {
              const items = editingCount
                ? (pack as ItemType)?.skuDetails?.items || []
                : (pack as SKU)?.items;

              const packItems =
                items
                  ?.map((i) => i.quantity)
                  ?.reduce((acc, current) => acc + current, 0) || 0;

              const totalItems =
                Number(field.value?.[index]?.quantity || '0') * packItems;

              return (
                <Flex
                  gap="10px"
                  key={
                    editingCount ? (pack as ItemType).sku : (pack as SKU)._id
                  }>
                  <Flex flex={1}>
                    <Text
                      w="full"
                      fontSize="14px"
                      p="8px 15px"
                      border="1px solid #ccc"
                      borderRadius="5px"
                      noOfLines={1}>
                      {editingCount
                        ? (pack as ItemType).skuDetails?.name
                        : (pack as SKU).name}
                    </Text>
                  </Flex>
                  <Flex flex={1}>
                    <FormikInput
                      type="number"
                      containerProps={{ mt: 0, isDisabled: isArchived }}
                      name={`sealedPackages[${index}].quantity`}
                    />
                  </Flex>
                  <Flex flex={1}>
                    <Input isDisabled bg="#DEDCDC" value={totalItems} />
                  </Flex>
                </Flex>
              );
            })}
          </Flex>

          <Flex mt="10px" gap="10px" w="full" fontSize="14px">
            <Flex flex={1}>
              <Text w="full" textAlign="right" fontWeight="bold">
                Subtotal
              </Text>
            </Flex>
            <Flex flex={1}>
              <Input isDisabled bg="#DEDCDC" value={sealedCasesSubTotal} />
            </Flex>
            <Flex flex={1}>
              <Input isDisabled bg="#DEDCDC" value={totalItemsSubTotal} />
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  );
};

const OpenItems: FC<CommonProps> = ({
  site,
  onCalculationsUpdate,
  isArchived = false,
  count,
}) => {
  const editingCount = count?.status !== InventoryCountStatus.SCHEDULED;
  const { data: itemsData = [], mutate, isLoading } = useSKUmtn(
    'item',
    undefined,
    site,
  );
  const { data: skuData = [] } = useSKU('package', undefined, site);
  const itemsSKU = editingCount ? count?.openItems || [] : itemsData;

  const packagesSKU: any[] = editingCount
    ? count?.sealedPackages || []
    : skuData;

  const [field, , helpers] = useField<FielValue>('openItems');
  const [sealedPackagesField] = useField<FielValue>('sealedPackages');
  const sealedPackages = sealedPackagesField?.value || [];

  useEffect(() => {
    if (!itemsSKU || count?.status !== InventoryCountStatus.SCHEDULED) {
      return;
    }

    mutate(undefined, {
      onSuccess: (_itemsSKU) => {
        const fieldValues = _itemsSKU?.map((i) => ({
          sku: i._id,
          quantity: '' as any,
          sealedQuantity: '' as any,
        }));

        helpers.setValue(fieldValues);
      },
    });
  }, [site]);

  const openItemsFieldValues = field.value.map((i) =>
    Number(i.quantity || '0'),
  );

  const openItemsCalculations = itemsSKU.map((itemSKU, index) => {
    const openItems = openItemsFieldValues[index];

    const currentItemId = editingCount
      ? (itemSKU as ItemType).sku
      : (itemSKU as SKU)._id;

    const packagesContainingThisItem =
      packagesSKU?.filter((p) => {
        const items = editingCount
          ? (p as ItemType).skuDetails?.items
          : (p as SKU).items;

        return items?.some((i) => i.sku === currentItemId);
      }) || [];
    const totalSealedItemsQuantity = packagesContainingThisItem.reduce(
      (acc, pkg) => {
        const items = editingCount
          ? (pkg as ItemType).skuDetails?.items
          : (pkg as SKU).items;
        const sealedPackage = sealedPackages.find(
          (p) =>
            items?.some((i) => i.sku === p.sku) ||
            (editingCount
              ? p.sku === (pkg as ItemType).sku
              : p.sku === (pkg as SKU)._id),
        );
        const sealedPackageQuantity = Number(sealedPackage?.quantity || '0');

        const itemQuantity = items?.reduce((_acc, _cur) => {
          if (_cur.sku === currentItemId) {
            _acc += _cur.quantity;
          }
          return _acc;
        }, 0);

        acc += (itemQuantity ?? 0) * sealedPackageQuantity;
        return acc;
      },
      0,
    );
    const totalItems = Number(openItems || '0') + totalSealedItemsQuantity;

    return {
      sku: currentItemId,
      openItems,
      totalItems,
      totalSealedItems: totalSealedItemsQuantity,
    };
  });

  onCalculationsUpdate?.(openItemsCalculations);

  const openItemsSubtotal =
    openItemsCalculations.reduce((acc, cur) => acc + cur.openItems, 0) || '0';
  const totalItemsSubtotal =
    openItemsCalculations.reduce((acc, cur) => acc + cur.totalItems, 0) || '0';
  const sealedItemsSubtotal =
    openItemsCalculations.reduce((acc, cur) => acc + cur.totalSealedItems, 0) ||
    '0';

  return (
    <Flex {...commonProps}>
      <Text {...headingProps}>Open Items</Text>

      {isLoading ? (
        <Center w="100%" flex={1} p="100px">
          <Spinner />
        </Center>
      ) : (
        <>
          <Flex gap="10px" w="full" fontSize="14px">
            <Flex flex={1.5}>
              <Text>Item SKUs</Text>
            </Flex>
            <Flex flex={1}>
              <Text>Open Item Count</Text>
            </Flex>
            <Flex flex={1}>
              <Text>Full item Count</Text>
            </Flex>
            <Flex flex={1}>
              <Text>Total Items</Text>
            </Flex>
          </Flex>

          <Flex direction="column" gap="10px" w="full">
            {itemsSKU?.map((item, index) => {
              return (
                <Flex
                  gap="10px"
                  key={
                    editingCount ? (item as ItemType).sku : (item as SKU)._id
                  }>
                  <Flex flex={1.5}>
                    <Text
                      w="full"
                      fontSize="14px"
                      p="8px 15px"
                      border="1px solid #ccc"
                      borderRadius="5px"
                      noOfLines={1}>
                      {editingCount
                        ? (item as ItemType).skuDetails?.name
                        : (item as SKU).name}
                    </Text>
                  </Flex>
                  <Flex flex={1}>
                    <FormikInput
                      type="number"
                      containerProps={{ mt: 0, isDisabled: isArchived }}
                      name={`openItems[${index}].quantity`}
                    />
                  </Flex>
                  <Flex flex={1}>
                    <Input
                      isDisabled
                      bg="#DEDCDC"
                      value={openItemsCalculations[index]?.totalSealedItems}
                    />
                  </Flex>
                  <Flex flex={1}>
                    <Input
                      isDisabled
                      bg="#DEDCDC"
                      value={openItemsCalculations[index]?.totalItems}
                    />
                  </Flex>
                </Flex>
              );
            })}
          </Flex>

          <Flex mt="10px" gap="10px" w="full" fontSize="14px">
            <Flex flex={1.5}>
              <Text w="full" textAlign="right" fontWeight="bold">
                Subtotal
              </Text>
            </Flex>
            <Flex flex={1}>
              <Input isDisabled bg="#DEDCDC" value={openItemsSubtotal} />
            </Flex>
            <Flex flex={1}>
              <Input isDisabled bg="#DEDCDC" value={sealedItemsSubtotal} />
            </Flex>
            <Flex flex={1}>
              <Input isDisabled bg="#DEDCDC" value={totalItemsSubtotal} />
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  );
};

const OpenCases: FC<CommonProps> = ({
  site,
  onCalculationsUpdate,
  isArchived = false,
  count,
}) => {
  const editingCount = count?.status !== InventoryCountStatus.SCHEDULED;

  const { data: casesData = [], mutate, isLoading } = useSKUmtn(
    'case',
    undefined,
    site,
  );
  const { data: packagesData = [] } = useSKU('package', undefined, site);

  const casesSKU = editingCount ? count?.openCases || [] : casesData;

  const packagesSKU: any[] = editingCount
    ? count?.sealedPackages || []
    : packagesData;

  const [field, , helpers] = useField<FielValue>('openCases');
  const [sealedPackagesField] = useField<FielValue>('sealedPackages');

  useEffect(() => {
    if (!casesSKU || count?.status !== InventoryCountStatus.SCHEDULED) {
      return;
    }

    mutate(undefined, {
      onSuccess: (_casesSKU) => {
        const fieldValues = _casesSKU?.map((i) => ({
          sku: i._id,
          quantity: '' as any,
          sealedQuantity: '' as any,
        }));

        helpers.setValue(fieldValues);
      },
    });
  }, [site]);

  const sealedPackages = sealedPackagesField?.value || [];

  const openCasesFieldValues = field.value.map((i) =>
    Number(i.quantity || '0'),
  );

  const openCasesCalculations = casesSKU.map((caseSKU, index) => {
    const openCases = openCasesFieldValues[index];

    const currentCaseId = editingCount
      ? (caseSKU as ItemType).sku
      : (caseSKU as SKU)._id;

    const packagesContainingThisCase =
      packagesSKU?.filter((p) => {
        const items = editingCount ? (p as ItemType).skuDetails : (p as SKU);
        return items?.caseSku === currentCaseId;
      }) || [];

    const totalSealedCasesQuantity = packagesContainingThisCase.reduce(
      (acc, pkg) => {
        const items = editingCount
          ? (pkg as ItemType).skuDetails?.items || []
          : (pkg as SKU).items;
        const sealedPackage = sealedPackages?.find((p) => {
          return (
            items?.some((i) => i.sku === p.sku) ||
            (editingCount
              ? p.sku === (pkg as ItemType).sku
              : p.sku === (pkg as SKU)._id)
          );
        });

        if (sealedPackage) {
          acc += Number(sealedPackage.quantity || '0');
        }

        return acc;
      },
      0,
    );

    const totalCases = Number(openCases || '0') + totalSealedCasesQuantity;

    return {
      sku: currentCaseId,
      openCases,
      totalCases,
      totalSealedCases: totalSealedCasesQuantity,
    };
  });

  onCalculationsUpdate?.(openCasesCalculations);

  const openCasesSubtotal =
    openCasesCalculations.reduce((acc, cur) => acc + cur.openCases, 0) || '0';
  const totalCasesSubtotal =
    openCasesCalculations.reduce((acc, cur) => acc + cur.totalCases, 0) || '0';
  const sealedCasesSubtotal =
    openCasesCalculations.reduce((acc, cur) => acc + cur.totalSealedCases, 0) ||
    '0';

  return (
    <Flex {...commonProps}>
      <Text {...headingProps}>Open Cases</Text>

      {isLoading ? (
        <Center w="100%" flex={1} p="100px">
          <Spinner />
        </Center>
      ) : (
        <>
          <Flex gap="10px" w="full" fontSize="14px">
            <Flex flex={1.5}>
              <Text>Case SKUs</Text>
            </Flex>
            <Flex flex={1}>
              <Text>Open Case Count</Text>
            </Flex>
            <Flex flex={1}>
              <Text>Full Case Count</Text>
            </Flex>
            <Flex flex={1}>
              <Text>Total Cases</Text>
            </Flex>
          </Flex>

          <Flex direction="column" gap="10px" w="full">
            {casesSKU?.map((_case, index) => {
              return (
                <Flex
                  gap="10px"
                  key={
                    editingCount ? (_case as ItemType).sku : (_case as SKU)._id
                  }>
                  <Flex flex={1.5}>
                    <Text
                      w="full"
                      fontSize="14px"
                      p="8px 15px"
                      border="1px solid #ccc"
                      borderRadius="5px"
                      noOfLines={1}>
                      {editingCount
                        ? (_case as ItemType).skuDetails?.name
                        : (_case as SKU).name}
                    </Text>
                  </Flex>
                  <Flex flex={1}>
                    <FormikInput
                      type="number"
                      containerProps={{ mt: 0, isDisabled: isArchived }}
                      name={`openCases[${index}].quantity`}
                    />
                  </Flex>
                  <Flex flex={1}>
                    <Input
                      isDisabled
                      bg="#DEDCDC"
                      value={openCasesCalculations[index].totalSealedCases}
                    />
                  </Flex>
                  <Flex flex={1}>
                    <Input
                      isDisabled
                      bg="#DEDCDC"
                      value={openCasesCalculations[index].totalCases}
                    />
                  </Flex>
                </Flex>
              );
            })}
          </Flex>

          <Flex mt="10px" gap="10px" w="full" fontSize="14px">
            <Flex flex={1.5}>
              <Text w="full" textAlign="right" fontWeight="bold">
                Subtotal
              </Text>
            </Flex>
            <Flex flex={1}>
              <Input isDisabled bg="#DEDCDC" value={openCasesSubtotal} />
            </Flex>
            <Flex flex={1}>
              <Input isDisabled bg="#DEDCDC" value={sealedCasesSubtotal} />
            </Flex>
            <Flex flex={1}>
              <Input isDisabled bg="#DEDCDC" value={totalCasesSubtotal} />
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  );
};
